import { transition } from "@theme/view-transition";

// <s-product-recommendations> lazily loads related/complementary products with Shopify's standard
// section rendering: once the element nears the viewport it requests
// data-url (the recommendations route with intent + limit) &product_id=…&section_id=<data-section-id>,
// takes the [data-recommendations-results] markup from the returned section HTML and swaps it into
// [data-recommendations-target], replacing the skeletons. Empty or failed responses hide the element.
// One component per file, framework-free.
class ProductRecommendations extends HTMLElement {
  #observer = null;
  #controller = null;

  connectedCallback() {
    if (!this.dataset.url || !this.dataset.productId || !this.dataset.sectionId) return;

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  async #load() {
    const target = this.querySelector("[data-recommendations-target]");
    if (!target) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const params = new URLSearchParams({
      product_id: this.dataset.productId,
      section_id: this.dataset.sectionId,
    });
    const url = `${this.dataset.url}&${params}`;

    this.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
      const html = await response.text();
      const results = new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector("[data-recommendations-results]");

      if (!results) {
        this.hidden = true;
        return;
      }
      await transition(() => target.replaceChildren(document.adoptNode(results)));
      this.dataset.performed = "true";
    } catch (error) {
      // Fetch failed: collapse quietly instead of showing skeletons forever.
      if (error.name !== "AbortError") this.hidden = true;
    } finally {
      this.removeAttribute("aria-busy");
      this.#controller = null;
    }
  }
}

if (!customElements.get("s-product-recommendations")) {
  customElements.define("s-product-recommendations", ProductRecommendations);
}
